import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Delete,
  Edit,
  Refresh,
  Search,
  Business,
  Campaign,
  CardMembership,
  CleaningServices
} from '@mui/icons-material';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import { useAuth } from '../contexts/AuthContext';
import api, { passkitAPI, companiesAPI, Company } from '../services/api';
import CleanResourceTemplatesDialog from '../components/Resources/CleanResourceTemplatesDialog';

// Types for resources (now coming from database with company info)
interface PassKitProgram {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  company_id: string;
  internal_id: string;
}

interface PassKitCampaign {
  id: string;
  name: string;
  description?: string;
  campaign_type: string;
  created_at: string;
  updated_at: string;
  company_id: string;
  internal_id: string;
}

interface PassKitProduction {
  id: string;
  name: string;
  description?: string;
  status: string;
  venue_name?: string;
  production_date?: string;
  created_at: string;
  updated_at: string;
  company_id: string;
  passkit_production_id: string;
}

type PassKitResource = PassKitProgram | PassKitCampaign | PassKitProduction;

type TabValue = 'programs' | 'campaigns' | 'productions';

const ResourceManagement: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!user;

  // Tab state
  const [currentTab, setCurrentTab] = useState<TabValue>('programs');

  // Data states
  const [passkitPrograms, setPasskitPrograms] = useState<PassKitProgram[]>([]);
  const [passkitCampaigns, setPasskitCampaigns] = useState<PassKitCampaign[]>([]);
  const [passkitProductions, setPasskitProductions] = useState<PassKitProduction[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);

  // Add Resource dialog states
  const [addResourceDialogOpen, setAddResourceDialogOpen] = useState(false);
  const [newResourceCompanyId, setNewResourceCompanyId] = useState<string>('');
  const [newResourceId, setNewResourceId] = useState('');
  const [newResourceType, setNewResourceType] = useState<'program' | 'campaign' | 'production'>('program');
  const [newResourceName, setNewResourceName] = useState('');
  const [newResourceDescription, setNewResourceDescription] = useState('');
  const [addResourceLoading, setAddResourceLoading] = useState(false);

  // Edit Resource dialog states
  const [editResourceDialogOpen, setEditResourceDialogOpen] = useState(false);
  const [editResourceName, setEditResourceName] = useState('');
  const [editResourceDescription, setEditResourceDescription] = useState('');
  const [editResourceLoading, setEditResourceLoading] = useState(false);

  // Success message
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Clean Templates dialog state
  const [cleanTemplatesDialogOpen, setCleanTemplatesDialogOpen] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [
        companiesResponse,
        programsResponse,
        campaignsResponse,
        productionsResponse
      ] = await Promise.all([
        companiesAPI.getAllCompanies(),
        passkitAPI.listPrograms(),
        passkitAPI.listCampaigns(),
        passkitAPI.listProductions()
      ]);

      setCompanies(companiesResponse.companies);
      setPasskitPrograms(programsResponse.programs || programsResponse || []);
      setPasskitCampaigns(campaignsResponse.campaigns || campaignsResponse || []);
      setPasskitProductions(productionsResponse.productions || productionsResponse || []);

    } catch (err: any) {
      logger.error('Error loading data:', err);
      setError(err.response?.data?.detail || err.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      loadData();
    }
  }, [isAuthenticated, isAdmin, loadData]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
    setPage(0);
    setSearchTerm('');
  };



  // Handle delete resource
  const handleUnlinkResource = async () => {
    if (!selectedResource) return;

    try {
      setUnlinkLoading(true);

      let endpoint = '';
      switch (currentTab) {
        case 'programs':
          endpoint = `programs/${selectedResource.internal_id}`;
          break;
        case 'campaigns':
          endpoint = `campaigns/${selectedResource.internal_id}`;
          break;
        case 'productions':
          endpoint = `productions/${selectedResource.internal_id}`;
          break;
      }

      await passkitAPI.unlinkResource(endpoint);

      setSuccessMessage('Resurs je uspešno obrisan');
      setShowSuccess(true);
      setUnlinkDialogOpen(false);
      setSelectedResource(null);

      // Reload data
      await loadData();

    } catch (err: any) {
      logger.error('Error deleting resource:', err);
      setError(err.message || 'Greška pri brisanju resursa');
    } finally {
      setUnlinkLoading(false);
    }
  };

  // Handle add new resource
  const handleAddResource = async () => {
    if (!newResourceCompanyId || !newResourceId || !newResourceName) {
      setError('Molimo unesite sve obavezne podatke');
      return;
    }

    try {
      setAddResourceLoading(true);
      setError('');

      let endpoint = '';
      let payload: any = {
        company_id: newResourceCompanyId,
        name: newResourceName,
        description: newResourceDescription || '',
      };

      switch (newResourceType) {
        case 'program':
          endpoint = 'link-program';
          payload.passkit_program_id = newResourceId;
          break;
        case 'campaign':
          endpoint = 'link-campaign';
          payload.passkit_campaign_id = newResourceId;
          break;
        case 'production':
          endpoint = 'link-production';
          payload.passkit_production_id = newResourceId;
          break;
      }

      await passkitAPI.linkResource(endpoint, payload);

      setSuccessMessage('Resurs je uspešno dodat');
      setShowSuccess(true);
      setAddResourceDialogOpen(false);

      // Reset form
      setNewResourceCompanyId('');
      setNewResourceId('');
      setNewResourceType('program');
      setNewResourceName('');
      setNewResourceDescription('');

      // Reload data
      await loadData();

    } catch (err: any) {
      logger.error('Error adding resource:', err);
      setError(err.message || 'Greška pri dodavanju resursa');
    } finally {
      setAddResourceLoading(false);
    }
  };

  // Handle edit resource
  const handleEditResource = async () => {
    if (!selectedResource || !editResourceName) {
      setError('Molimo unesite naziv resursa');
      return;
    }

    try {
      setEditResourceLoading(true);
      setError('');

      let endpoint = '';
      let payload = {
        name: editResourceName,
        description: editResourceDescription || '',
      };

      switch (currentTab) {
        case 'programs':
          endpoint = `programs/${selectedResource.internal_id}`;
          break;
        case 'campaigns':
          endpoint = `campaigns/${selectedResource.internal_id}`;
          break;
        case 'productions':
          endpoint = `productions/${selectedResource.internal_id}`;
          break;
      }

      await api.patch(`/resources/${endpoint}`, payload);

      setSuccessMessage('Resurs je uspešno ažuriran');
      setShowSuccess(true);
      setEditResourceDialogOpen(false);
      setSelectedResource(null);

      // Reset form
      setEditResourceName('');
      setEditResourceDescription('');

      // Reload data
      await loadData();

    } catch (err: any) {
      logger.error('Error editing resource:', err);
      setError(err.message || 'Greška pri ažuriranju resursa');
    } finally {
      setEditResourceLoading(false);
    }
  };

  // Clean Templates success handler
  const handleCleanSuccess = () => {
    setSuccessMessage('Nekorišćeni template-i su uspešno obrisani!');
    setShowSuccess(true);
    loadData(); // Reload resources
  };

  // Get current data based on tab
  const getCurrentData = (): { passkitResources: PassKitResource[], resourceType: string, icon: any } => {
    switch (currentTab) {
      case 'programs':
        return {
          passkitResources: Array.isArray(passkitPrograms) ? passkitPrograms : [],
          resourceType: 'Program',
          icon: CardMembership
        };
      case 'campaigns':
        return {
          passkitResources: Array.isArray(passkitCampaigns) ? passkitCampaigns : [],
          resourceType: 'Kampanja',
          icon: Campaign
        };
      case 'productions':
        return {
          passkitResources: Array.isArray(passkitProductions) ? passkitProductions : [],
          resourceType: 'Produkcija',
          icon: Business
        };
      default:
        return {
          passkitResources: [],
          resourceType: '',
          icon: Business
        };
    }
  };

  const { passkitResources, resourceType, icon: TabIcon } = getCurrentData();

  // Filter and paginate data
  const filteredResources = useMemo(() => {
    if (!Array.isArray(passkitResources)) {
      logger.error('passkitResources is not an array:', passkitResources);
      return [];
    }
    return passkitResources.filter(resource =>
      resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [passkitResources, searchTerm]);

  const paginatedResources = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredResources.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredResources, page, rowsPerPage]);

  // Get company name for a resource
  const getResourceCompanyName = (companyId: string) => {
    const company = companies.find(c => c.company_id.toString() === companyId);
    return company ? company.company_name : 'Nepoznata kompanija';
  };



  if (!isAuthenticated || !isAdmin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography variant="h6">Nemate ovlašćenja za pristup ovoj stranici</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ResponsivePageHeader
        title="Upravljanje Resursima"
        subtitle="Upravljajte Programs i Campaigns i povezujte ih sa kompanijama"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => setAddResourceDialogOpen(true)}
              disabled={loading}
            >
              Dodaj Resurs
            </Button>
            <Button
              variant="outlined"
              startIcon={<CleaningServices />}
              onClick={() => setCleanTemplatesDialogOpen(true)}
              color="warning"
            >
              Očisti Template-e
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadData}
              disabled={loading}
            >
              Osveži
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Programs"
            value="programs"
            icon={<CardMembership />}
            iconPosition="start"
          />
          <Tab
            label="Campaigns"
            value="campaigns"
            icon={<Campaign />}
            iconPosition="start"
          />
          <Tab
            label="Productions"
            value="productions"
            icon={<Business />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
          <Typography variant="h4" color="primary">{passkitResources.length}</Typography>
          <Typography variant="body2">Ukupno {resourceType}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
          <Typography variant="h4" color="success.main">
            {passkitResources.filter(r => r.company_id).length}
          </Typography>
          <Typography variant="body2">Aktivno</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: '200px', textAlign: 'center' }}>
          <Typography variant="h4" color="info.main">
            {new Set(passkitResources.map(r => r.company_id)).size}
          </Typography>
          <Typography variant="body2">Kompanija</Typography>
        </Paper>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder={`Pretraži ${resourceType.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
      </Paper>

      {/* Resources Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Naziv</TableCell>
                <TableCell>Opis</TableCell>
                <TableCell>Kompanija</TableCell>
                <TableCell>Kreiran</TableCell>
                <TableCell align="right">Akcije</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResources.map((resource) => {
                const companyName = getResourceCompanyName(resource.company_id);

                return (
                  <TableRow key={resource.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TabIcon sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {resource.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {resource.description || 'Nema opisa'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={companyName}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(resource.created_at).toLocaleDateString('sr-RS')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Uredi resurs">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedResource(resource);
                              setEditResourceName(resource.name);
                              setEditResourceDescription(resource.description || '');
                              setEditResourceDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Obriši resurs">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedResource(resource);
                              setUnlinkDialogOpen(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredResources.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Redova po stranici:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} od ${count}`}
        />
      </Paper>



      {/* Unlink Resource Dialog */}
      <Dialog
        open={unlinkDialogOpen}
        onClose={() => setUnlinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Potvrdi brisanje</DialogTitle>
        <DialogContent>
          <Typography>
            Da li ste sigurni da želite da obrišete resurs "{selectedResource?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ova akcija je trajna i ne može se poništiti.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)}>Otkaži</Button>
          <Button
            onClick={handleUnlinkResource}
            variant="contained"
            color="error"
            disabled={unlinkLoading}
          >
            {unlinkLoading ? <CircularProgress size={20} /> : 'Obriši'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Resource Dialog */}
      <Dialog
        open={editResourceDialogOpen}
        onClose={() => setEditResourceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Uredi Resurs</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Resource Name */}
            <TextField
              fullWidth
              required
              label="Naziv Resursa"
              value={editResourceName}
              onChange={(e) => setEditResourceName(e.target.value)}
              placeholder="Unesite naziv resursa"
            />

            {/* Resource Description */}
            <TextField
              fullWidth
              label="Opis"
              value={editResourceDescription}
              onChange={(e) => setEditResourceDescription(e.target.value)}
              placeholder="Unesite opis resursa"
              multiline
              rows={3}
            />

            {/* Info about what can't be changed */}
            <Typography variant="body2" color="text.secondary">
              Kompanija i Resource ID se ne mogu menjati nakon kreiranja.
            </Typography>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditResourceDialogOpen(false)}>Otkaži</Button>
          <Button
            onClick={handleEditResource}
            variant="contained"
            disabled={!editResourceName || editResourceLoading}
          >
            {editResourceLoading ? <CircularProgress size={20} /> : 'Sačuvaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Resource Dialog */}
      <Dialog
        open={addResourceDialogOpen}
        onClose={() => setAddResourceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dodaj Novi Resurs</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Company Selection */}
            <FormControl fullWidth required>
              <InputLabel>Kompanija</InputLabel>
              <Select
                value={newResourceCompanyId}
                label="Kompanija"
                onChange={(e) => setNewResourceCompanyId(e.target.value as string)}
              >
                {companies.map((company) => (
                  <MenuItem key={company.company_id} value={company.company_id.toString()}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Resource Type Selection */}
            <FormControl fullWidth required>
              <InputLabel>Tip Resursa</InputLabel>
              <Select
                value={newResourceType}
                label="Tip Resursa"
                onChange={(e) => setNewResourceType(e.target.value as 'program' | 'campaign' | 'production')}
              >
                <MenuItem value="program">Membership (Program)</MenuItem>
                <MenuItem value="campaign">Coupon (Campaign)</MenuItem>
                <MenuItem value="production">Tickets (Production)</MenuItem>
              </Select>
            </FormControl>

            {/* Resource ID from PassKit */}
            <TextField
              fullWidth
              required
              label="Resource ID (from PassKit)"
              value={newResourceId}
              onChange={(e) => setNewResourceId(e.target.value)}
              placeholder="Kopirajte ID iz PassKit-a"
              helperText="Kopirajte Resource ID iz PassKit interfejsa"
            />

            {/* Resource Name */}
            <TextField
              fullWidth
              required
              label="Naziv Resursa"
              value={newResourceName}
              onChange={(e) => setNewResourceName(e.target.value)}
              placeholder="Unesite naziv resursa"
            />

            {/* Optional Description */}
            <TextField
              fullWidth
              label="Opis (opciono)"
              value={newResourceDescription}
              onChange={(e) => setNewResourceDescription(e.target.value)}
              placeholder="Unesite opis resursa"
              multiline
              rows={3}
            />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddResourceDialogOpen(false)}>Otkaži</Button>
          <Button
            onClick={handleAddResource}
            variant="contained"
            disabled={!newResourceCompanyId || !newResourceId || !newResourceName || addResourceLoading}
          >
            {addResourceLoading ? <CircularProgress size={20} /> : 'Dodaj Resurs'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clean Templates Dialog */}
      <CleanResourceTemplatesDialog
        open={cleanTemplatesDialogOpen}
        onClose={() => setCleanTemplatesDialogOpen(false)}
        onSuccess={handleCleanSuccess}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        message={successMessage}
      />
    </Box>
  );
};

export default ResourceManagement;
